import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Box,
  Autocomplete,
  Typography,
} from "@mui/material";
import { ReactComponent as gpsIcon } from "assets/gps.svg";
import { useTheme } from "@emotion/react";
import CustomInputField from "ui-components/formik/CustomInputField";
import { Form, FormikProvider, useFormik } from "formik";
import PerfectScrollbar from "react-perfect-scrollbar";
import {
  FetchAllBankAccountTypes,
  FetchAllBanks,
  FetchAllCountries,
} from "api";
import * as Yup from "yup";
import { containerRef } from "utils";
import { Primary } from 'ui-components/Buttons';

const numberInputProps = {
  inputMode: "numeric", // Use numeric keyboard on mobile devices
  pattern: "[0-9]*", // Only allow digits 0-9
  // title: "Please enter a number between 0 and 9",
  style: {
    // textAlign: 'center', // Center the text in the input
  },
  min: 0,
  onKeyDown: (event) => {
    // Prevent the minus sign (-) from being entered
    if (event.key === "-") {
      event.preventDefault();
    }
  },
  onKeyPress: (event) => {
    // Prevent any input when the minus key is pressed, as it was already prevented in the onKeyDown event
    if (event.key === "-") {
      event.preventDefault();
    }
  },
  onKeyPress: (event) => {
    // Get the input value
    const inputValue = event.key;

    // Allow only numeric characters (0-9) and certain control keys
    const allowedKeys = [
      "0",
      "1",
      "2",
      "3",
      "4",
      "5",
      "6",
      "7",
      "8",
      "9",
      "Backspace",
      "ArrowLeft",
      "ArrowRight",
      "Delete",
      "Tab",
    ];

    // Check if the pressed key is in the allowedKeys array
    if (!allowedKeys.includes(inputValue)) {
      // If not, prevent the key from being entered
      event.preventDefault();
    }
  },
  onInput: (event) => {
    // Get the input value
    const inputValue = event.target.value;

    // Remove non-numeric characters using a regular expression
    const numericValue = inputValue.replace(/[^0-9]/g, "");

    // Update the input field value with the filtered numeric value
    event.target.value = numericValue;
  },
  onKeyDown: (event) => {
    // Prevent the input of minus sign (key code 189)
    if (event.keyCode === 189 || event.key === "-") {
      event.preventDefault();
    }
  },
  disableUnderline: true,
};

const BankDetailsPopup = ({ open, handleClose, handleSubmit }) => {
  const [validationObj, setValidationObj] = React.useState({});
  const bankValidationSchema = Yup.object({ ...validationObj });
  const {
    data: countries,
    isRefetching: isRefetchingCountries,
    isLoading: isLoadingCountries,
    refetch: refetchCountries,
  } = FetchAllCountries({ search: "" });
  const {
    data: banks,
    isRefetching: isRefetchingBanks,
    isLoading: isLoadingBanks,
    refetch: refetchBanks,
  } = FetchAllBanks();
  const {
    data: bankAccountTypes,
    isRefetching: isRefetchingBankAccountTypes,
    isLoading: isLoadingBankAccountTypes,
    refetch: refetchBankAccountTypes,
  } = FetchAllBankAccountTypes();
  const formik = useFormik({
    initialValues: {
      accountName: "",
      accountNumber: "",
      ifscCode: "",
      bankName: "",
      bankAddress: "",
      country: null,
      bank_id: "",
      account_type: "",
    },
    validationSchema: bankValidationSchema,
    enableReinitialize: true,
    validateOnChange: true,
    onSubmit: (values) => {
      handleClose();
    },
  });

  const theme = useTheme();

  const handleSelectionCountryChange = (event, value) => {
    formik.setFieldValue(
      "country",
      value ? countries?.find((item) => item?._id === value?._id) : ""
    );
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    formik.setValues({ ...formik.values, [name]: value });
  };

  // const handleSave = () => {
  //     handleSubmit()
  //     console.log(formik.values); // You can replace this with an API call
  // };

  const isIndianAccount =
    String(formik.values?.country?.label)?.toLowerCase() === "india";
  // console.log(isIndianAccount, "ceded")

  React.useEffect(() => {
    setValidationObj({
      //   bankName: Yup.string().required("Bank name is required"),
      bankAddress: Yup.string().required("Bank address is required"),
      account_type: Yup.object().required("Account type is required"),
      accountName: Yup.string().required("Account name is required"),
      accountNumber: Yup.string().required("Account number is required"),
      ifscCode: Yup.string().required(
        `${isIndianAccount ? "Ifsc" : "Swift"} code is required`
      ),
      country: Yup.object().required("Country is required"),
      //   bank_id: Yup.object().required("Please select a bank"),
    });
  }, [isIndianAccount]);

  const placeholders = {
    accountName: "Enter account name",
    accountNumber: "Enter account number",
    ifscCode: isIndianAccount ? "Enter ifsc code" : "Enter swift code",
    bankName: isIndianAccount ? "Select bank" : "Enter bank name",
    bankAddress: "Enter bank address",
    country: "Select country",
    account_type: "Select account type",
  };

  return (
    <FormikProvider value={formik}>
      <Form onSubmit={formik.handleSubmit}>
        <DialogTitle sx={{ marginInline: "auto" }}>
          <Typography
            sx={{
              fontSize: "1.5rem",
              fontWeight: 700,
              lineHeight: "28px",
              color: theme.palette.customColors.g300,
              paddingLeft: '0.5rem'
            }}
          >
            Bank Details
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ paddingBottom: 0 , paddingInline: 0}}>
          <PerfectScrollbar
          containerRef={containerRef}
            style={{
              maxHeight: "calc(-250px + 100vh)",
              paddingInline: "2rem",
            }}
          >
            <Grid item container xs={12} columnSpacing={2}>
              <Grid item xs={12} md={6}>
                {/* {!isLoadingCountries && !isRefetchingCountries ? */}
                <Autocomplete
                  id="country-select-demo"
                  options={countries}
                  autoHighlight
                  fullWidth
                  size="small"
                  getOptionLabel={(option) => String(option.label)}
                  // getOptionLabel={(option) => String(option.label)?.length > 25 ? String(option.label)?.substring(0,25) + "..." : String(option.label)}
                  onChange={(e, newValue) =>
                    formik.setFieldValue("country", newValue)
                  }
                  renderInput={(params) => (
                    <CustomInputField
                      {...params}
                      name={"country"}
                      fullWidth
                      label="COUNTRY"
                      placeholder={placeholders?.country}
                      value={""}
                      sx={{
                        "& > .MuiInputBase-root": {
                          padding: "0.65rem 0.7rem !important",
                        },
                        // padding: '0 !important',
                        width: "100%",
                        marginBottom: "0.5rem",
                      }}
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          autoComplete: "new-password", // disable autocomplete and autofill
                        },
                      }}
                    />
                  )}
                />
                {/* :  */}
                {/* <>
                                <Box className="skeleton" sx={{ height: 20, width: '100%', marginBottom: 1 }}></Box>
                                <Box className="skeleton" sx={{ height: 52.38, width: '100%', marginBottom: 2 }}></Box>
                            </> */}
                {/* } */}
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomInputField
                  // margin="dense"
                  label="ACCOUNT NAME"
                  name="accountName"
                  value={formik.values?.accountName}
                  onChange={handleChange}
                  fullWidth
                  placeholder={placeholders?.accountName}
                  sx={{ marginBottom: "0.5rem" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <>
                  {/* {console.log(banks,"FetchAllBankAccountTypes")} */}
                  <Autocomplete
                    id="country-select-demo"
                    options={bankAccountTypes ? bankAccountTypes : []}
                    autoHighlight
                    fullWidth
                    size="small"
                    getOptionLabel={(option) => option.label}
                    onChange={(e, newValue) =>
                      formik.setFieldValue("account_type", newValue)
                    }
                    renderInput={(params) => (
                      <CustomInputField
                        {...params}
                        name={"account_type"}
                        fullWidth
                        label="Account Type"
                        placeholder={placeholders?.account_type}
                        value={""}
                        sx={{
                          "& > .MuiInputBase-root": {
                            padding: "0.65rem 0.7rem !important",
                          },
                          // padding: '0 !important',
                          width: "100%",
                          marginBottom: "0.5rem",
                        }}
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            autoComplete: "new-password", // disable autocomplete and autofill
                          },
                        }}
                      />
                    )}
                  />
                </>
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomInputField
                  // margin="dense"
                  InputProps={{ ...numberInputProps }}
                  label="ACCOUNT NUMBER"
                  name="accountNumber"
                  value={formik.values?.accountNumber}
                  onChange={handleChange}
                  fullWidth
                  placeholder={placeholders?.accountNumber}
                  sx={{ marginBottom: "0.5rem" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <CustomInputField
                  // margin="dense"
                  InputProps={{ ...numberInputProps }}
                  label={isIndianAccount ? "IFSC Code" : "Swift Code"}
                  name="ifscCode"
                  value={formik.values?.ifscCode}
                  onChange={handleChange}
                  fullWidth
                  placeholder={placeholders?.ifscCode}
                  sx={{ marginBottom: "0.5rem" }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                {isIndianAccount ? (
                  <>
                    <Autocomplete
                      id="country-select-demo"
                      options={banks ? banks : []}
                      autoHighlight
                      fullWidth
                      size="small"
                      getOptionLabel={(option) => option?.label}
                      onChange={(e, newValue) =>
                        formik.setFieldValue("bank_id", newValue)
                      }
                      renderInput={(params) => (
                        <CustomInputField
                          {...params}
                          name={"bank_id"}
                          fullWidth
                          label="Bank Name"
                          placeholder={placeholders?.bankName}
                          value={""}
                          sx={{
                            "& > .MuiInputBase-root": {
                              padding: "0.65rem 0.7rem !important",
                            },
                            // padding: '0 !important',
                            width: "100%",
                            marginBottom: "0.5rem",
                          }}
                          slotProps={{
                            htmlInput: {
                              ...params.inputProps,
                              autoComplete: "new-password", // disable autocomplete and autofill
                            },
                          }}
                        />
                      )}
                    />
                  </>
                ) : (
                  <CustomInputField
                    // margin="dense"
                    label="Bank Name"
                    name="bankName"
                    value={formik.values?.bankName}
                    onChange={handleChange}
                    fullWidth
                    placeholder={placeholders?.bankName}
                    sx={{ marginBottom: "0.5rem" }}
                  />
                )}
              </Grid>
              <Grid item xs={12}>
                <CustomInputField
                  // margin="dense"
                  label="Bank Address"
                  name="bankAddress"
                  value={formik.values?.bankAddress}
                  onChange={handleChange}
                  sx={{ marginBottom: "0.5rem" }}
                  fullWidth
                  placeholder={placeholders?.bankAddress}
                />
              </Grid>
            </Grid>
          </PerfectScrollbar>
        </DialogContent>
        <DialogActions
          sx={{ paddingInline: "1.5rem", paddingBottom: "1.5rem" }}
        >
          {/* <Button onClick={handleClose}>Cancel</Button> */}
          <Primary
            type="submit"
            fullWidth
            sx={{ padding: "0.75rem 1.25rem", fontSize: "1rem" }}
          >
            Update
          </Primary>
          {/* <Primary sx={{ background: theme.palette.customColors.indigo700, padding: "0.625rem 1rem", width: '100%' }} onClick={handleSave}>Save</Primary> */}
        </DialogActions>
        {/* </Dialog> */}
      </Form>
    </FormikProvider>
  );
};

export default BankDetailsPopup;
